import { create } from 'zustand'

export type MessageRole = 'user' | 'assistant' | 'system'

export type AgentType = 'coder' | 'designer' | 'architect' | 'reviewer'

export interface FileChange {
  path: string
  action: 'create' | 'modify' | 'delete'
  content?: string
}

export interface AgentAction {
  agent: AgentType
  type: 'thinking' | 'writing' | 'analyzing' | 'complete'
  message?: string
  fileChanges?: FileChange[]
}

export interface ChatMessage {
  id: string
  role: MessageRole
  content: string
  timestamp: Date
  agentActions?: AgentAction[]
  isStreaming?: boolean
  imageUrl?: string
}

interface ChatStore {
  messages: ChatMessage[]
  isLoading: boolean
  activeAgent: AgentType | null
  
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => string
  updateMessage: (id: string, updates: Partial<ChatMessage>) => void
  setLoading: (loading: boolean) => void
  setActiveAgent: (agent: AgentType | null) => void
  clearMessages: () => void
  appendToMessage: (id: string, content: string) => void
  addAgentAction: (messageId: string, action: AgentAction) => void
}

export const useChatStore = create<ChatStore>((set, get) => ({
  messages: [],
  isLoading: false,
  activeAgent: null,

  addMessage: (message) => {
    const id = crypto.randomUUID()
    set((state) => ({
      messages: [
        ...state.messages,
        {
          ...message,
          id,
          timestamp: new Date(),
        },
      ],
    }))
    return id
  },

  updateMessage: (id, updates) => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === id ? { ...msg, ...updates } : msg
      ),
    }))
  },

  appendToMessage: (id, content) => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === id ? { ...msg, content: msg.content + content } : msg
      ),
    }))
  },

  addAgentAction: (messageId, action) => {
    set((state) => ({
      messages: state.messages.map((msg) =>
        msg.id === messageId
          ? {
              ...msg,
              agentActions: [...(msg.agentActions || []), action],
            }
          : msg
      ),
    }))
  },

  setLoading: (loading) => set({ isLoading: loading }),
  
  setActiveAgent: (agent) => set({ activeAgent: agent }),

  clearMessages: () => set({ messages: [] }),
}))

// Agent configurations
export const AGENTS: Record<AgentType, { name: string; icon: string; description: string; color: string }> = {
  coder: {
    name: 'Coder',
    icon: 'Code',
    description: 'Writes and modifies code',
    color: 'text-blue-400',
  },
  designer: {
    name: 'Designer', 
    icon: 'Palette',
    description: 'Handles styling and UI',
    color: 'text-pink-400',
  },
  architect: {
    name: 'Architect',
    icon: 'Boxes',
    description: 'Plans structure and components',
    color: 'text-amber-400',
  },
  reviewer: {
    name: 'Reviewer',
    icon: 'Search',
    description: 'Reviews and suggests improvements',
    color: 'text-green-400',
  },
}
